/**
 * Fonctions pures extraites de useEventStore : transformations de données sans état
 * ni accès au store (set/get). Isolées ici pour alléger le store et pouvoir les
 * tester indépendamment. Aucune de ces fonctions ne doit toucher au state Zustand.
 */

export const ACTIVE_STATUSES = ['pending', 'play_next'];

/** Alertes donateurs (fil « En direct ») : on ne garde pas plus de 5 min. */
export const DONOR_FEED_MAX_AGE_MS = 5 * 60 * 1000;

export function pruneDonorFeedEntries(feed, now = Date.now()) {
  return (feed ?? []).filter((item) => {
    const at = Number(item?.at) || 0;
    return now - at < DONOR_FEED_MAX_AGE_MS;
  });
}

export function pulsePatchForDonationLoad(retainPulse) {
  return retainPulse ? {} : { donationGoalPulse: 0 };
}

/**
 * Montants associés aux suggestions : uniquement dans public.bids. On agrège ici pour l’UI (bid_amount, is_boosted).
 */
export function aggregateBidsByRequestId(bids) {
  const m = new Map();
  for (const b of bids ?? []) {
    const rid = b.request_id;
    if (!rid) continue;
    let agg = m.get(rid);
    if (!agg) {
      agg = { paidCents: 0, pendingCents: 0, bidCount: 0 };
      m.set(rid, agg);
    }
    const amt = Math.max(0, Number(b.amount) || 0);
    if (b.payment_status === 'paid') agg.paidCents += amt;
    else if (b.payment_status === 'pending') agg.pendingCents += amt;
    if (b.payment_status !== 'refunded') agg.bidCount += 1;
  }
  for (const agg of m.values()) {
    agg.displayCents = agg.paidCents + agg.pendingCents;
  }
  return m;
}

/**
 * priority_score : votes + floor(montant bids payés ou en attente / 10), aligné sur refresh_request_priority_score côté DB.
 */
export function computePriority(row) {
  const votes = Math.max(0, Number(row?.votes) || 0);
  const bid = Math.max(0, Number(row?.bid_amount) || 0);
  return votes + Math.floor(bid / 10);
}

export function normalizeRequest(row, bidByRequest) {
  if (!row) return row;
  const agg = bidByRequest.get(row.id);
  const bid_amount = agg?.displayCents ?? 0;
  const merged = {
    ...row,
    bid_amount,
    is_boosted: bid_amount >= 500,
    boost_threshold: 500,
    bid_count: agg?.bidCount ?? 0,
  };
  return { ...merged, priority_score: computePriority(merged) };
}

export function normalizeRequests(list, bids) {
  const bidByRequest = aggregateBidsByRequestId(bids);
  return (list ?? []).map((r) => normalizeRequest(r, bidByRequest));
}

export function sortByPriority(list) {
  return [...list].sort((a, b) => {
    const pa = computePriority(a);
    const pb = computePriority(b);
    if (pb !== pa) return pb - pa;
    return new Date(a.created_at) - new Date(b.created_at);
  });
}

export function presenceCount(state) {
  if (!state) return 0;
  return Object.values(state).reduce(
    (acc, presences) => acc + (Array.isArray(presences) ? presences.length : 0),
    0
  );
}
